import { DashboardStatModel } from '../types/statistics';

interface StatisticsRowData {
  label: string;
  mer: number;
  mar: number;
  lun: number;
  dom: number;
  sab: number;
  ven: number;
  gio: number;
}

// Mappa i giorni della settimana italiani ai loro indici
const dayNameMap: { [key: string]: keyof Omit<StatisticsRowData, 'label'> } = {
  'lunedì': 'lun',
  'martedì': 'mar', 
  'mercoledì': 'mer',
  'giovedì': 'gio',
  'venerdì': 'ven',
  'sabato': 'sab',
  'domenica': 'dom'
};

// Mappa i nomi delle colonne del database ai nomi visualizzati
const metricDisplayNames: { [key: string]: string } = {
  'accessi': 'Accessi',
  'registrazioni': 'Registrazioni', 
  'dieteNuove': 'Diete Nuove',
  'dieteSett': 'Diete Sett',
  'dieteGior': 'Diete Gior',
  'conversioni': 'Conversioni',
  'alimenti': 'Alimenti'
};

// Ordine delle metriche come nella vista originale
const metricOrder = [
  'accessi',
  'registrazioni', 
  'dieteNuove',
  'dieteSett',
  'dieteGior',
  'conversioni',
  'alimenti'
];

/**
 * Trasforma i dati API (righe per data) nel formato richiesto dal componente (colonne per giorno)
 * Genera sempre 7 giorni consecutivi (da oggi indietro) e cerca i dati corrispondenti nell'API
 */
export const mapApiDataToTableFormat = (apiData: DashboardStatModel[], yearsOffset: number = 0): StatisticsRowData[] => {
  const result: StatisticsRowData[] = [];

  // Verifica che apiData sia un array valido
  if (!apiData || !Array.isArray(apiData)) {
    console.warn('mapApiDataToTableFormat: apiData non è un array valido', apiData);
    apiData = [];
  }

  // Genera le 7 date consecutive (oggi, ieri, l'altro ieri, etc.) con l'offset anni
  const last7Days: { date: Date; dayKey: keyof Omit<StatisticsRowData, 'label'> }[] = [];
  const columnKeys: (keyof Omit<StatisticsRowData, 'label'>)[] = ['mer', 'mar', 'lun', 'dom', 'sab', 'ven', 'gio'];
  
  for (let i = 0; i < 7; i++) {
    const date = new Date();
    date.setDate(date.getDate() - i);
    // Applica l'offset degli anni
    date.setFullYear(date.getFullYear() + yearsOffset);
    last7Days.push({
      date,
      dayKey: columnKeys[i]
    });
  }

  // Crea un indice dei dati API per data (formato YYYY-MM-DD)
  const apiDataByDate: { [dateKey: string]: DashboardStatModel } = {};
  apiData.forEach(item => {
    if (item.data) {
      const dateKey = item.data; // Assumendo formato "YYYY-MM-DD"
      apiDataByDate[dateKey] = item;
    }
  });

  // Per ogni metrica, crea una riga
  metricOrder.forEach(metric => {
    const row: StatisticsRowData = {
      label: metricDisplayNames[metric],
      mer: 0,
      mar: 0,
      lun: 0,
      dom: 0,
      sab: 0,
      ven: 0,
      gio: 0
    };

    // Per ogni giorno degli ultimi 7, cerca i dati corrispondenti
    last7Days.forEach(({ date, dayKey }) => {
      const dateKey = date.toISOString().split('T')[0]; // Formato YYYY-MM-DD
      const apiItem = apiDataByDate[dateKey];
      
      if (apiItem) {
        // Se ci sono dati per questo giorno, usa il valore della metrica
        const value = (apiItem as any)[metric] || 0;
        row[dayKey] = value;
      } else {
        // Se non ci sono dati per questo giorno, lascia 0 (già impostato)
        row[dayKey] = 0;
      }
    });

    result.push(row);
  });

  return result;
};

/**
 * Genera le intestazioni delle colonne seguendo la logica della view originale
 * mer (oggi), mar, lun, dom, sab, ven, gio
 */
export const generateColumnHeaders = (): string[] => {
  const headers: string[] = [];
  
  for (let i = 0; i < 7; i++) {
    const date = new Date();
    date.setDate(date.getDate() - i);
    const dayName = date.toLocaleDateString('it-IT', { weekday: 'short' });
    
    if (i === 0) {
      headers.push(`${dayName} (oggi)`);
    } else {
      headers.push(dayName);
    }
  }
  
  return headers;
};

/**
 * Trasforma i dati settimanali in formato appropriato
 * Mappa i dati settimanali aggregati alle colonne della tabella
 */
export const mapWeeklyDataToTableFormat = (apiData: DashboardStatModel[]): StatisticsRowData[] => {
  const result: StatisticsRowData[] = [];

  // Verifica che apiData sia un array valido
  if (!apiData || !Array.isArray(apiData)) {
    console.warn('mapWeeklyDataToTableFormat: apiData non è un array valido', apiData);
    return result;
  }

  // Ordiniamo i dati per data decrescente (settimana più recente prima)
  const sortedData = [...apiData].sort((a, b) => 
    new Date(b.data).getTime() - new Date(a.data).getTime()
  );

  // Mappa le prime 7 settimane alle colonne (mer, mar, lun, dom, sab, ven, gio)
  const columnKeys: (keyof Omit<StatisticsRowData, 'label'>)[] = ['mer', 'mar', 'lun', 'dom', 'sab', 'ven', 'gio'];

  // Per ogni metrica, crea una riga
  metricOrder.forEach(metric => {
    const row: StatisticsRowData = {
      label: metricDisplayNames[metric],
      mer: 0,
      mar: 0,
      lun: 0,
      dom: 0,
      sab: 0,
      ven: 0,
      gio: 0
    };

    // Assegna ogni settimana a una colonna diversa
    sortedData.slice(0, 7).forEach((item, index) => {
      if (index < columnKeys.length) {
        const columnKey = columnKeys[index];
        const value = (item as any)[metric] || 0;
        row[columnKey] = value;
      }
    });

    result.push(row);
  });

  return result;
};